<script setup lang="ts">
import type { CategoryPage } from '~/types/database'

const props = defineProps<{
  category: CategoryPage
}>()

/**
 * Generate dynamic brand styles using CSS custom properties
 */
const brandStyle = computed(() => {
  const color = props.category.brand_color || '#E31837'
  return {
    '--brand-color': color,
    '--brand-color-dark': `color-mix(in srgb, ${color} 80%, black)`,
    '--brand-color-light': `color-mix(in srgb, ${color} 20%, white)`,
    background: `linear-gradient(135deg, var(--brand-color), var(--brand-color-dark))`
  }
})
</script>

<template>
  <section
    class="relative text-white overflow-hidden"
    :style="brandStyle"
    role="banner"
    :aria-label="`Sección de ${category.name}`"
  >
    <!-- Background Pattern -->
    <div class="absolute inset-0 opacity-10" aria-hidden="true">
      <svg
        class="w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <pattern
          id="hero-grid"
          width="10"
          height="10"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 10 0 L 0 0 0 10"
            fill="none"
            stroke="white"
            stroke-width="0.5"
          />
        </pattern>
        <rect width="100" height="100" fill="url(#hero-grid)" />
      </svg>
    </div>

    <UContainer class="relative py-10 md:py-14 lg:py-16">
      <div class="flex flex-col md:flex-row items-start md:items-center gap-6">
        <!-- Brand Logo (Premium Brands) -->
        <div
          v-if="category.is_premium_brand && category.brand_logo_url"
          class="flex-shrink-0"
        >
          <img
            :src="category.brand_logo_url"
            :alt="`Logo ${category.name}`"
            class="h-14 md:h-18 lg:h-20 w-auto object-contain filter brightness-0 invert"
            loading="eager"
            decoding="async"
          />
        </div>

        <!-- Content -->
        <div class="flex-1 min-w-0">
          <!-- Premium Badge -->
          <span
            v-if="category.is_premium_brand"
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur-sm mb-3"
          >
            <UIcon name="i-heroicons-star-solid" class="w-3 h-3" />
            Marca Premium
          </span>

          <!-- Title -->
          <h1 class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4 leading-tight">
            {{ category.hero_title || category.name }}
          </h1>

          <!-- Description -->
          <p
            v-if="category.hero_description"
            class="text-base sm:text-lg md:text-xl opacity-90 max-w-3xl leading-relaxed"
          >
            {{ category.hero_description }}
          </p>
        </div>

        <!-- Hero Image (Desktop) -->
        <div
          v-if="category.hero_image_url"
          class="hidden lg:block flex-shrink-0"
        >
          <img
            :src="category.hero_image_url"
            :alt="category.name"
            class="w-40 h-40 xl:w-48 xl:h-48 object-contain drop-shadow-lg"
            loading="eager"
            decoding="async"
          />
        </div>
      </div>
    </UContainer>
  </section>
</template>
